// Per-faction Core defenses (GDD §15, R1). IC's defense ('built-in') lives in the
// shared combat paths (Royal Ballista, Murder Slots, Last Course of Stone) and is
// untouched — it is the control group. Each other faction gets an isolated block
// driven by its FactionDef.coreDefense config, implemented as its faction lands.
// Cooldowns live in PlayerState.coreDefenseCooldowns.

import { factionDef, type CoreDefenseConfig } from '../../content/factions';
import { buildingDef, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import { dist } from '../../shared/vec';
import type { Sim } from '../sim';
import type { Building, PlayerState, Squad } from '../state';

const dt = TUNING.dt;

/**
 * Grasping Roots + Living Bark (VW-01). Root capacity is counted in SQUADS, not
 * individual models (documented adaptation of the Bible's per-model count to the
 * squad-pool health model): cap 2, +1 with the Rootheart Chamber annex.
 */
function stepVerdant(
  sim: Sim,
  p: PlayerState,
  core: Building,
  cfg: Extract<CoreDefenseConfig, { kind: 'verdant' }>,
): void {
  const bd = buildingDef(core.defId);
  const radius = Math.max(bd.footprint.w, bd.footprint.h) / 2 + 4;
  const cap = cfg.rootSquadCap + sim.annexBonus(p.id, 'rootCapacityBonus');
  let rooted = 0;
  for (const s of sim.state.squads.values()) {
    if (rooted >= cap) break;
    if (!sim.areHostile(s.owner, p.id)) continue;
    const d = unitDef(s.defId);
    const smallMelee =
      (d.sizeTag === 'small' || d.sizeTag === 'medium') &&
      (d.attack?.range ?? 0) <= 3 &&
      !d.roleTags.includes('siege') &&
      !s.isBoss;
    if (!smallMelee) continue;
    if (dist(s.pos, core.pos) > radius) continue;
    // wrap and lash: continuous re-root while inside the grasp
    s.rootedRemaining = Math.max(s.rootedRemaining, 0.25);
    sim.damageSquad(s, cfg.rootDps * dt, { isRanged: false, attackerOwner: p.id });
    rooted++;
  }
  // Living Bark: regeneration after 12 s of quiet, paused by fire
  if (
    core.health < core.maxHealth &&
    core.fireSuppressedRemaining <= 0 &&
    sim.state.time - core.lastDamagedAt > 12
  ) {
    core.health = Math.min(core.maxHealth, core.health + core.maxHealth * cfg.barkRegenPerSecond * dt);
  }
}

/** Grave Shades + Dread Pulse + Throne Feast (AH-01), fueled by stored corpse marks. */
function stepAshen(
  sim: Sim,
  p: PlayerState,
  core: Building,
  cfg: Extract<CoreDefenseConfig, { kind: 'ashen' }>,
): void {
  const cd = p.coreDefenseCooldowns;
  // Grave Shades: when the Core is under attack, consume a stored mark → 2 shades
  if (
    (cd['shades'] ?? 0) <= 0 &&
    p.corpseStored > 0 &&
    sim.state.time - core.lastDamagedAt < 3
  ) {
    p.corpseStored--;
    cd['shades'] = cfg.shadeCooldown;
    for (let i = 0; i < cfg.shadesPerMark; i++) {
      const angle = (i / cfg.shadesPerMark) * Math.PI * 2;
      sim.spawnSquad(
        cfg.shadeUnitId,
        p.id,
        { x: core.pos.x + Math.cos(angle) * 6, y: core.pos.y + Math.sin(angle) * 6 },
        { temporarySeconds: cfg.shadeDuration },
      );
    }
    sim.pushEvent({
      type: 'AbilityTriggered',
      id: core.id,
      abilityId: 'grave-shades',
      pos: { ...core.pos },
      label: 'Grave Shades',
    });
  }
  // Dread Pulse: fear wave vs clustered small/medium attackers
  if ((cd['dread'] ?? 0) <= 0) {
    let clustered = 0;
    for (const s of sim.state.squads.values()) {
      if (!sim.areHostile(s.owner, p.id)) continue;
      const d = unitDef(s.defId);
      if (d.sizeTag !== 'small' && d.sizeTag !== 'medium') continue;
      if (dist(s.pos, core.pos) <= cfg.dreadPulseRadius) clustered++;
    }
    if (clustered >= 3) {
      cd['dread'] = cfg.dreadPulseCooldown;
      for (const s of sim.state.squads.values()) {
        if (!sim.areHostile(s.owner, p.id)) continue;
        const d = unitDef(s.defId);
        if (d.sizeTag !== 'small' && d.sizeTag !== 'medium') continue;
        if (dist(s.pos, core.pos) <= cfg.dreadPulseRadius) {
          s.buffs.push({ stat: 'attackSpeed', mult: 0.8, remaining: 6 });
        }
      }
      sim.pushEvent({
        type: 'AbilityTriggered',
        id: core.id,
        abilityId: 'dread-pulse',
        pos: { ...core.pos },
        label: 'Dread Pulse',
      });
    }
  }
  // Throne Feast: below threshold, eat stored marks to heal
  if (
    (cd['feast'] ?? 0) <= 0 &&
    p.corpseStored > 0 &&
    core.health / core.maxHealth < cfg.throneFeastThreshold
  ) {
    p.corpseStored--;
    cd['feast'] = cfg.throneFeastCooldown;
    core.health = Math.min(core.maxHealth, core.health + cfg.throneFeastHealPerMark);
    sim.pushEvent({
      type: 'AbilityTriggered',
      id: core.id,
      abilityId: 'throne-feast',
      pos: { ...core.pos },
      label: 'Throne Feast',
    });
  }
}

/**
 * Repulsion Cycle + Prismatic Decoy (AD-01). Spell Mantle and Worker Translation are
 * documented deviations (not yet modeled).
 */
function stepAstral(
  sim: Sim,
  p: PlayerState,
  core: Building,
  cfg: Extract<CoreDefenseConfig, { kind: 'astral' }>,
): void {
  const cd = p.coreDefenseCooldowns;
  const bd = buildingDef(core.defId);
  const radius = Math.max(bd.footprint.w, bd.footprint.h) / 2 + 5;
  // Repulsion Cycle: fold clustered small/medium melee away from the Core
  if ((cd['repulsion'] ?? 0) <= 0) {
    const clusterList: Squad[] = [];
    const resistantList: Squad[] = [];
    for (const s of sim.state.squads.values()) {
      if (!sim.areHostile(s.owner, p.id)) continue;
      const d = unitDef(s.defId);
      const smallMelee =
        (d.sizeTag === 'small' || d.sizeTag === 'medium') && (d.attack?.range ?? 0) <= 3 && !s.isBoss;
      if (dist(s.pos, core.pos) > radius) continue;
      if (smallMelee) clusterList.push(s);
      else if (d.sizeTag === 'large' || d.sizeTag === 'giant') resistantList.push(s);
    }
    // L/G resist the displacement but still trigger the timed cycle and receive one
    // non-stacking slow. Previously this slow was appended every tick while the cooldown
    // was ready, compounding 0.7^n until giants effectively had zero move speed.
    if (clusterList.length >= cfg.repulsionClusterSize || resistantList.length > 0) {
      // Phase Barrier annex: the cycle recharges faster
      const haste = sim.annexEffect(p.id, 'repulsionHaste');
      cd['repulsion'] = Math.max(2, cfg.repulsionCooldown - (haste?.seconds ?? 0));
      const displacedSlow = sim.annexEffect(p.id, 'displacedSlow');
      if (clusterList.length >= cfg.repulsionClusterSize) {
        for (const s of clusterList) {
          // displace away from the Core along the outward ray (R3: teleportSquad
          // re-validates walkability and clears path state)
          const d = Math.max(0.001, dist(s.pos, core.pos));
          const dir = { x: (s.pos.x - core.pos.x) / d, y: (s.pos.y - core.pos.y) / d };
          sim.teleportSquad(
            s,
            { x: s.pos.x + dir.x * cfg.repulsionDistance, y: s.pos.y + dir.y * cfg.repulsionDistance },
            cfg.repulsionDistance,
          );
          // Phase Barrier annex: displaced Small/Medium attackers are slowed
          if (displacedSlow) {
            const source = `phase-barrier-${core.id}`;
            const existing = s.buffs.find((buff) => buff.source === source);
            if (existing) {
              existing.mult = 1 - displacedSlow.magnitude;
              existing.remaining = Math.max(existing.remaining, displacedSlow.duration);
            } else {
              s.buffs.push({
                stat: 'moveSpeed',
                mult: 1 - displacedSlow.magnitude,
                remaining: displacedSlow.duration,
                source,
              });
            }
          }
        }
      }
      const slowSource = `core-repulsion-${core.id}`;
      for (const s of resistantList) {
        const existing = s.buffs.find((buff) => buff.source === slowSource);
        if (existing) {
          existing.mult = 0.7;
          existing.remaining = Math.max(existing.remaining, 4);
        } else {
          s.buffs.push({ stat: 'moveSpeed', mult: 0.7, remaining: 4, source: slowSource });
        }
      }
      sim.pushEvent({
        type: 'AbilityTriggered',
        id: core.id,
        abilityId: 'repulsion-cycle',
        pos: { ...core.pos },
        label: 'Repulsion Cycle',
      });
    }
  }
  // Prismatic Decoy: false images at each health threshold (once per threshold)
  const frac = core.health / core.maxHealth;
  for (let i = 0; i < cfg.decoyThresholds.length; i++) {
    const key = `decoy${i}`;
    if ((cd[key] ?? 0) > 0) continue;
    if (frac > cfg.decoyThresholds[i]) continue;
    cd[key] = 99999; // once per match
    for (const side of [-1, 1]) {
      sim.spawnBuilding('b-ad-decoy', p.id, { x: core.pos.x + side * 10, y: core.pos.y + 6 }, {
        complete: true,
        expiresSeconds: cfg.decoyDuration,
      });
    }
    sim.pushEvent({
      type: 'AbilityTriggered',
      id: core.id,
      abilityId: 'prismatic-decoy',
      pos: { ...core.pos },
      label: 'Prismatic Decoy',
    });
  }
}

export function stepCoreDefense(sim: Sim): void {
  for (const p of sim.state.players) {
    for (const key of Object.keys(p.coreDefenseCooldowns)) {
      if (p.coreDefenseCooldowns[key] > 0) p.coreDefenseCooldowns[key] -= dt;
    }
    const cfg = factionDef(p.faction).coreDefense;
    if (cfg.kind === 'built-in') continue; // IC: shared combat paths
    const core = sim.coreAlive(p.id);
    if (!core) continue;
    switch (cfg.kind) {
      case 'ashen':
        stepAshen(sim, p, core, cfg);
        break;
      case 'verdant':
        stepVerdant(sim, p, core, cfg);
        break;
      case 'astral':
        stepAstral(sim, p, core, cfg);
        break;
    }
  }
}
